import { all, call, put, select } from 'redux-saga/effects';
import serviceLocator, { SSAP_SVC } from '../../APIs/common/service-locator';
import { bloodRelationshipsSaga } from './bloodrelationships-saga';
import { flowSaga } from './flow-saga';
import { ssapSettingsSaga } from './ssap-settings-saga';

import {
    getIfFlowLoaded,
    getIsState,
    getSsapFlow,
    selectFirstMatchedPage,
} from '../../reducers/appReducer';

import {
    changePage,
    fetchApplicationFailure,
    fetchApplicationSuccess,
    setDataStructure,
    setException,
    setFlow,
    setSessionInitialData,
    showExtraFinalSummary
} from '../../actions/ssap/ssapActionCreators';

// Utils

import { localStorageHyperDashboardTools } from '../../utils/tools/localStorageHyperDashboardTools';
import { processJsonTools } from '../../utils/tools/processJsonTools';

// Constants

import { APPLICATION_STATUSES } from '../../constants/common/applicationStatusesAndModes';
import { configs } from '../../constants/configs/configs';
import { EXCEPTIONS } from '../../constants/ssap/exceptions/ssapExceptions';
import { flowTypes as FLOW_TYPES } from '../../constants/ssap/flows/flowTypes';
import { getCachedFlows } from '../../constants/ssap/flows/pagesList';
import { PAGES } from '../../constants/ssap/flows/pagesTypes';
import { SSAP_FLOW_VERSION } from '../../constants/ssap/miscellaneous/miscellaneous';

export function* initialBootstrapSaga() {

    // TODO: extract <taxHouseholdType> somewhere to configs
    const taxHouseholdType = 'object';
    const bloodRelationshipPlace = 'household';

    const {
        OPEN,
        UNCLAIMED
    } = APPLICATION_STATUSES;

    try {
        yield all([
            call(bloodRelationshipsSaga),
            call(flowSaga)
        ]);

        const isFlowLoaded = yield select(getIfFlowLoaded);
        const ssapFlow = yield select(getSsapFlow);
        const isState = yield select(getIsState);

        if(isFlowLoaded) {
            const ssapService = yield call(serviceLocator, SSAP_SVC);

            const ssapApplicationId = configs.ENV.EXPLICIT_PRODUCTION
                ? window.ssapApplicationObj.ssapId
                : localStorageHyperDashboardTools.getValueByPropName('mockJson', 'threeHhms');

            const data = yield call([ssapService, ssapService.getInitialData], { ssapApplicationId });

            if(data) {
                const updatePageValues = data => {
                    const mockCurrentPage = localStorageHyperDashboardTools
                        .getValueByPropName('currentPage', 0);

                    const mockMaxAchievedPage = localStorageHyperDashboardTools
                        .getValueByPropName('maxAchievedPage', 0);

                    if(global.__ENV__.DEVELOPMENT) {
                        data.currentPage = mockCurrentPage;
                        data.maxAchievedPage = mockMaxAchievedPage;
                    }
                    return data;
                };

                let {
                    applicationStatus,
                    applyingForFinancialAssistanceIndicator,
                    currentPage,
                    maxAchievedPage,
                    uiProperties
                } = data ? updatePageValues(data) : { uiProperties: '{}' };

                const {
                    ssapFlowVersion = 1
                } = JSON.parse(uiProperties);

                switch(true) {
                    case ssapFlowVersion === 1 && SSAP_FLOW_VERSION === 2:
                        if(currentPage >= 5) {
                            currentPage = currentPage - 1;
                        }
                }

                let currentFlow = FLOW_TYPES.NON_FINANCIAL_FLOW_NON_SWITCHABLE;

                switch (true) {

                    case ssapFlow === FLOW_TYPES.NON_FINANCIAL_FLOW_NON_SWITCHABLE:
                        currentFlow = FLOW_TYPES.NON_FINANCIAL_FLOW_NON_SWITCHABLE;
                        break;

                    case ssapFlow === FLOW_TYPES.NON_FINANCIAL_FLOW_SWITCHABLE
                    && maxAchievedPage < 4 && applyingForFinancialAssistanceIndicator:
                        currentFlow = FLOW_TYPES.FINANCIAL_FLOW;
                        yield put(setFlow(FLOW_TYPES.FINANCIAL_FLOW));
                        break;

                    case ssapFlow === FLOW_TYPES.NON_FINANCIAL_FLOW_SWITCHABLE
                    && maxAchievedPage < 4:
                        currentFlow = FLOW_TYPES.NON_FINANCIAL_FLOW_SWITCHABLE;
                        yield put(setFlow(FLOW_TYPES.NON_FINANCIAL_FLOW_SWITCHABLE));
                        break;

                    case ssapFlow === FLOW_TYPES.NON_FINANCIAL_FLOW_SWITCHABLE
                    && maxAchievedPage >= 4
                    && !applyingForFinancialAssistanceIndicator:
                        currentFlow = FLOW_TYPES.NON_FINANCIAL_FLOW_SWITCHABLE;
                        yield put(setFlow(FLOW_TYPES.NON_FINANCIAL_FLOW_SWITCHABLE));
                        break;

                    case ssapFlow === FLOW_TYPES.NON_FINANCIAL_FLOW_SWITCHABLE
                    && maxAchievedPage >= 4
                    && applyingForFinancialAssistanceIndicator:
                        currentFlow = FLOW_TYPES.FINANCIAL_FLOW;
                        yield put(setFlow(FLOW_TYPES.FINANCIAL_FLOW));
                        break;

                }

                const { applicationType, coverageYear } = configs.ENV.EXPLICIT_PRODUCTION
                    ? window.ssapApplicationObj
                    : { applicationType: null, coverageYear: null };

                const resolvedCoverageYear = (coverageYear !== null && coverageYear !== '')
                    ? +coverageYear
                    : data.coverageYear;

                yield call(ssapSettingsSaga, { year: resolvedCoverageYear });

                const isExtraFinalSummaryShown = (applicationStatus === OPEN || applicationStatus === UNCLAIMED)
                    ? data.isEditApplication === true
                    : false;

                if(isExtraFinalSummaryShown) {
                    yield put(showExtraFinalSummary(true));
                }

                const cachedFlow = getCachedFlows({
                    flowType: currentFlow,
                    hhmsQty: processJsonTools.resolveTaxHousehold(data).householdMember.length,
                    coverageYear: resolvedCoverageYear,
                    isExtraFinalSummaryShown
                });

                const partialAppState = {
                    ssap: {
                        adjustedFlow: cachedFlow,
                        data
                    }
                };

                const finalReviewPageNumber = selectFirstMatchedPage(
                    partialAppState,
                    PAGES.FINAL_APPLICATION_REVIEW_PAGE
                );

                const getMaxAchievedPage = data => {
                    switch(true) {
                        case data.manuallyCreated === false && data.maxAchievedPage === 0:
                            return finalReviewPageNumber + 1;

                        default:
                            return data.maxAchievedPage;
                    }
                };

                let updatedData = {
                    ...data,
                    finalized: false,
                    applicationType: (applicationType !== null && applicationType !== '')
                        ? applicationType
                        : data.applicationType,
                    coverageYear: resolvedCoverageYear,
                    maxAchievedPage: getMaxAchievedPage(data)
                };

                yield put(setDataStructure({
                    'taxHouseholdStructure': processJsonTools.getTaxHouseholdType({ ...updatedData }),
                    'bloodRelationshipStructure': processJsonTools.getBloodRelationshipPlace({ ...updatedData })
                }));

                yield put(
                    setSessionInitialData(
                        processJsonTools
                            .convertBloodRelationships(
                                processJsonTools.convertTaxHousehold(updatedData, taxHouseholdType),
                                bloodRelationshipPlace,
                                undefined,
                                (
                                    applicationStatus !== APPLICATION_STATUSES.OPEN
                                    && applicationStatus !== APPLICATION_STATUSES.UNCLAIMED
                                )
                            )
                    )
                );

                if(isState.MN){
                    yield put(setException(
                        EXCEPTIONS.REVIEW_AFTER_SIGN,
                        [ finalReviewPageNumber ],
                        true
                    ));
                    yield put(fetchApplicationSuccess());
                    return null;
                }

                switch(applicationStatus) {
                    case APPLICATION_STATUSES.OPEN:
                    case APPLICATION_STATUSES.UNCLAIMED:
                        yield put(changePage(currentPage, 'increase'));
                        break;
                    default:
                        yield put(setException(
                            EXCEPTIONS.REVIEW_AFTER_SIGN,
                            [ finalReviewPageNumber ],
                            true
                        ));
                }
                yield put(fetchApplicationSuccess());
            }
        }
    } catch(e) {
        yield put(fetchApplicationFailure());
    }
}